import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import CheckboxTree from "react-checkbox-tree";
import {useGetMediaTreeQuery} from "../../drafts/api/draftsApiSlice";
import {useGetCmEnumsQuery} from "../../../app/appApiSlice";
import {MediaNode} from "../../drafts/types";
import {MediumAssignment} from "../types";
import {getUuid} from "../../../config/general";
import useDebounce from "../../../hooks/useDebounce";

interface EditMediumsModalProps {
    open: boolean;
    assignment: MediumAssignment | null;
    onClose: () => void;
    onSave: (mediaFormat: string, mediaIds: number[]) => void;
}

const EditMediumsModal: React.FC<EditMediumsModalProps> = ({open, assignment, onClose, onSave}) => {
    const [mediaFormat, setMediaFormat] = useState('');
    const [nodes, setNodes] = useState<any[]>([]);
    const [filteredNodes, setFilteredNodes] = useState<any[]>([]);
    const [checkedNodes, setCheckedNodes] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [treeKey, setTreeKey] = useState(Math.random());
    const [filterText, setFilterText] = useState('');
    const [mediaLength, setMediaLength] = useState<number | null>(null);
    const [pendingCheckedMediaIds, setPendingCheckedMediaIds] = useState<number[] | null>(null);

    const {data: cmEnums} = useGetCmEnumsQuery();
    const {
        currentData: mediaTree,
        isFetching: loadingMediaTree
    } = useGetMediaTreeQuery(mediaFormat || '', {skip: !mediaFormat});

    let mediaSize = 0;
    let flattenNodeValues: string[] = [];

    const buildMediaTreeCheckboxNodes: any = (tree: MediaNode[]) => {
        return tree.map((element: MediaNode, index) => {
            if (element.children && element.children.length > 0) {
                let val = element.id + '_' + element.type + getUuid(index);
                let place = '';

                if (element.type === 'Media::Agglomeration') {
                    place = '(Aglomeracja)';
                } else if (element.type === 'Media::City') {
                    place = '(Miasto)';
                }

                return {
                    value: val,
                    label: element.name + ' ' + `${place}`,
                    children: buildMediaTreeCheckboxNodes(element.children),
                };
            } else {
                let val = element.id + '_' + element.type + getUuid(index);
                mediaSize += 1;
                flattenNodeValues.push(val);
                return {
                    value: val,
                    label: element.asId ? element.asId + ' - ' + element.name : element.name,
                };
            }
        });
    };

    const filterNodes = (filtered: any, node: any) => {
        if (node.label.toLocaleLowerCase().match(filterText.toString().toLocaleLowerCase())) {
            filtered.push(node);
            return filtered;
        }
        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodes, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    };

    useEffect(() => {
        if (open && assignment) {
            setFilterText('');
            setCheckedNodes([]);
            setNodes([]);
            setFilteredNodes([]);
            setMediaLength(null);
            setPendingCheckedMediaIds(assignment.mediaIds);
            setMediaFormat(assignment.mediaFormat);
        } else if (!open) {
            setMediaFormat('');
            setCheckedNodes([]);
            setNodes([]);
            setFilteredNodes([]);
            setMediaLength(null);
            setPendingCheckedMediaIds(null);
            setFilterText('');
        }
    }, [open, assignment]);

    useEffect(() => {
        if (mediaTree) {
            mediaSize = 0;
            const mediaTreeCheckboxNodes = buildMediaTreeCheckboxNodes(mediaTree);
            const leafValues = [...flattenNodeValues];
            setTimeout(() => {
                setNodes(mediaTreeCheckboxNodes);
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: mediaTreeCheckboxNodes
                }]);
                setMediaLength(mediaSize);
                if (pendingCheckedMediaIds) {
                    setCheckedNodes(leafValues.filter((value) =>
                        pendingCheckedMediaIds.includes(parseInt(value.split('_')[0]))));
                    setPendingCheckedMediaIds(null);
                    setTreeKey(Math.random());
                }
            }, 200);
        }
    }, [mediaTree]);

    useDebounce(() => {
        if (!filterText) {
            setFilteredNodes([{
                label: 'Wszystkie nośniki',
                value: 'all',
                children: nodes
            }]);
            return;
        }
        setFilteredNodes([{
            label: 'Wszystkie nośniki',
            value: 'all',
            children: nodes.reduce(filterNodes, [])
        }]);
    }, [filterText], 100);

    const uniqueCheckedMediaIds = Array.from(new Set(checkedNodes
        .filter(el => el !== 'all')
        .map((node: string) => parseInt(node.split('_')[0]))
        .filter(id => !isNaN(id))));

    const handleSave = () => {
        if (uniqueCheckedMediaIds.length === 0) {
            return;
        }
        onSave(mediaFormat, uniqueCheckedMediaIds);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
        >
            <DialogTitle>
                Edytuj przypisanie nośników
            </DialogTitle>
            <DialogContent>
                {assignment && (
                    <Box sx={{mb: 2, mt: 1}}>
                        <Typography variant="body2" color="text.secondary">
                            {assignment.principalType === 'User' ? 'Użytkownik' : 'Grupa'}
                        </Typography>
                        <Typography variant="subtitle1">{assignment.principalLabel}</Typography>
                    </Box>
                )}

                <FormControl fullWidth size="small" sx={{mb: 2}}>
                    <InputLabel shrink id="edit-media-format-select-label">Format nośnika</InputLabel>
                    <Select
                        variant="outlined"
                        labelId="edit-media-format-select-label"
                        id="edit-media-format-select"
                        label="Format nośnika"
                        value={mediaFormat}
                        notched
                        onChange={(event) => {
                            setCheckedNodes([]);
                            setNodes([]);
                            setFilteredNodes([]);
                            setMediaLength(null);
                            setPendingCheckedMediaIds(null);
                            setMediaFormat(event.target.value);
                        }}
                    >
                        <MenuItem value="">
                            <em>Brak</em>
                        </MenuItem>
                        {cmEnums && (cmEnums.find(cmEnum => cmEnum.name === 'mediaFormats')?.values as string[]).map((format) => (
                            <MenuItem key={format} value={format}>{format}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Wybrane nośniki: {uniqueCheckedMediaIds.length}/{mediaLength || 0}
                </Typography>

                {loadingMediaTree && (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                        <CircularProgress size={100}/>
                    </Box>
                )}

                <Box sx={{minHeight: '300px'}}>
                    {!loadingMediaTree && mediaTree && (
                        <>
                            <TextField
                                value={filterText}
                                label="Wyszukaj..."
                                onInput={(event: any) => setFilterText(event.target.value)}
                                variant="outlined"
                                sx={{mt: 1, mb: 3}}
                                fullWidth
                            />
                            <CheckboxTree
                                key={treeKey}
                                showNodeTitles={true}
                                nodes={filteredNodes}
                                checked={checkedNodes}
                                expanded={expanded}
                                showExpandAll={true}
                                onCheck={(checked: any) => {
                                    setCheckedNodes(checked);
                                    setTreeKey(Math.random());
                                }}
                                onExpand={(exp: any) => {
                                    setExpanded(exp);
                                }}
                            />
                        </>
                    )}
                    {!loadingMediaTree && mediaTree && mediaTree.length === 0 && (
                        <Typography color="text.secondary">Brak dostępnych nośników</Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Anuluj</Button>
                <Button
                    variant="contained"
                    disabled={uniqueCheckedMediaIds.length === 0}
                    onClick={handleSave}
                >
                    Zapisz zmiany
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditMediumsModal;
